import { ChangeEvent } from 'react'
import { Checkbox, Selector } from '@/components/core'
import { Button } from '@/components'
import {
  KeyMessageFormSection,
  KeyMessageTitle,
  KeyMessageTitleWithCounter,
  KeyMessageTextArea,
  KeyMessageCountSpan,
  FormGrid,
  AddButtonContainer,
  Advice,
  ApplyToContainer,
  BrandAndSourceRow,
  BrandSelectorContainer,
} from '../KeyMessage.styles'

interface KeyMessageFormProps {
  premises: string
  message: string
  exclusionPremises: string
  onExclusionPremisesChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  isLoading: boolean
  onPremisesChange: (value: string) => void
  onMessageChange: (value: string) => void
  onSubmit: () => void
  applyToSocial: boolean
  applyToPress: boolean
  onApplyToSocialChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onApplyToPressChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  brandBinder: any
  brandOptions: { text: string; value: string | number }[]
  isLoadingBrands: boolean
}

export function KeyMessageForm({
  premises,
  message,
  exclusionPremises,
  onExclusionPremisesChange,
  isLoading,
  onPremisesChange,
  onMessageChange,
  onSubmit,
  applyToSocial,
  applyToPress,
  onApplyToSocialChange,
  onApplyToPressChange,
  brandBinder,
  brandOptions,
  isLoadingBrands,
}: KeyMessageFormProps): JSX.Element {
  const premisesCount = premises.trim() ? premises.split(';').filter((p) => p.trim().length > 0).length : 0
  const messageLength = message.trim().length

  function handlePremisesChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    onPremisesChange(event.target.value)
  }

  function handleMessageChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    onMessageChange(event.target.value)
  }

  return (
    <FormGrid>
      <KeyMessageFormSection>
        <KeyMessageTitleWithCounter>
          <KeyMessageTitle>
            Mensagem <Advice>*obrigatório</Advice>
          </KeyMessageTitle>
          <KeyMessageCountSpan>{messageLength} caractere(s)</KeyMessageCountSpan>
        </KeyMessageTitleWithCounter>
        <KeyMessageTextArea
          placeholder="Digite a mensagem-chave que será associada às premissas acima"
          value={message}
          onChange={handleMessageChange}
          style={{ minHeight: '80px' }}
        />
      </KeyMessageFormSection>
      <KeyMessageFormSection>
        <KeyMessageTitleWithCounter>
          <KeyMessageTitle>
            Premissas <Advice>*obrigatório</Advice>
          </KeyMessageTitle>
          <KeyMessageCountSpan>{premisesCount} premissa(s)</KeyMessageCountSpan>
        </KeyMessageTitleWithCounter>
        <KeyMessageTextArea
          placeholder="Digite as premissas separadas por ponto e vírgula (;)&#10;Exemplo: inovação; tecnologia; transformação digital"
          value={premises}
          onChange={handlePremisesChange}
        />
      </KeyMessageFormSection>
      <KeyMessageFormSection>
        <KeyMessageTitle>
          Não aplicar mensagem-chave quando: <Advice>(opcional)</Advice>
        </KeyMessageTitle>
        <KeyMessageTextArea
          placeholder="Digite as premissas de quando a mensagem-chave não deve ser aplicada"
          value={exclusionPremises}
          onChange={onExclusionPremisesChange}
        />
      </KeyMessageFormSection>

      <BrandAndSourceRow>
        <KeyMessageFormSection>
          <KeyMessageTitle>
            Marcas <Advice>*obrigatório</Advice>
          </KeyMessageTitle>
          <BrandSelectorContainer>
            <Selector.Multi
              binder={brandBinder}
              options={brandOptions}
              loading={isLoadingBrands}
              required
              placeholder="Selecione uma ou mais marcas"
              $fit
            />
          </BrandSelectorContainer>
        </KeyMessageFormSection>

        <KeyMessageFormSection>
          <ApplyToContainer>
            <KeyMessageTitle>Aplicar para:</KeyMessageTitle>
            <Checkbox checked={applyToSocial} onChange={onApplyToSocialChange} color="green" label="Redes Sociais" />
            <Checkbox checked={applyToPress} onChange={onApplyToPressChange} color="green" label="Imprensa" />
          </ApplyToContainer>
        </KeyMessageFormSection>
      </BrandAndSourceRow>
      <AddButtonContainer>
        <Button.Root
          $variant="primary"
          onClick={onSubmit}
          $loading={isLoading}
          disabled={!premises.trim() || !message.trim() || isLoading}
          $disabledMessage={isLoading ? 'Salvando dados, aguarde...' : undefined}>
          <Button.Text value="Adicionar" />
        </Button.Root>
      </AddButtonContainer>
    </FormGrid>
  )
}
